import { v4 as uuid } from 'uuid';
import { BaseEntityQuery } from '../base-entity.query';
import { BaseEntityService } from '../base-entity.service';
import { Filter } from '../filter/filter.model';
import { tableCollectionTemplate } from './base-templates/table/table.data';
import { TableCollectionTemplate } from './base-templates/table/table.model';

export interface Collection {
	id: string;
	templates: CollectionTemplate[];
	filters: Filter[];
	sortOptions: SortOption[];

	query: BaseEntityQuery;
	service: BaseEntityService;
	getMethod?: string; // The service method to call to retrieve the entities
	methodArgs?: any; // Arguments to pass to the service method
	queryParams: QueryParams;
	resultsPath?: string; // The path to the results in the response
	totalResultsPath?: string; // The path to the total results in the response
	additionalColumnsPath?: string; // The path to the additional column definitions in the response
	currentTemplate: CollectionTemplate | TableCollectionTemplate;

	layoutLocationCode: LayoutLocationCode | any;
	exportable: boolean;
	quickViews: boolean;
	getEntitiesOnParamsChange: boolean;
	appendEntities: boolean;
	showCreateButton: boolean;

	// Akita State Settings
	entities: any[];
	selectedEntities: any[];
	loadingEntities: any[];
	cursor: string;
	totalResults: number;
	additionalColumns: any[];

	sidebarOpen: boolean;
	loaded: boolean;
	showPaginator: boolean;

	paginationMode?: 'more' | 'paginator';
}

export function createCollection(params: Partial<Collection>): Collection {
	return {
		id: uuid(),
		templates: [tableCollectionTemplate],
		currentTemplate: tableCollectionTemplate,
		filters: [],
		sortOptions: [],
		queryParams: {},
		getEntitiesOnParamsChange: true,
		selectedEntities: [],
		loadingEntities: [],
		loaded: false,
		showPaginator: true,
		limit: 10,
		page: 1,
		...params
	} as Collection;
}

export enum LayoutLocationCode {
	DASHBOARD_ENROLLMENT_PERIODS = 'DASHBOARD_ENROLLMENT_PERIODS',
	TACTIC_MANAGER = 'TACTIC_MANAGER',
	PRODUCTION_TACTICS = 'PRODUCTION_TACTICS',
	DASHBOARD_BUSINESSES = 'DASHBOARD_BUSINESSES',
	DASHBOARD_PAST_ENROLLMENT = 'DASHBOARD_PAST_ENROLLMENT',
	DASHBOARD_ENROLLMENTS = 'DASHBOARD_ENROLLMENTS',
	ENROLL_SELECT_BUSINESSES = 'ENROLL_SELECT_BUSINESSES',
	ENROLLMENT_MANAGER = 'ENROLLMENT_MANAGER',
	ENROLLMENT_PACKAGES = 'ENROLLMENT_PACKAGES',
	ADMIN_USERS = 'ADMIN_USERS',
	BUSINESS = 'BUSINESS',
	CAMPAIGNS = 'CAMPAIGNS',
	TACTICS = 'TACTICS',
	CAMPAIGN_MANAGER = 'CAMPAIGN_MANAGER'
}

export interface CollectionTemplate {
	id: string;
	slug: string; // Slug is what determines the component used.
	name: string;
	settings: CollectionTemplateSettings;
}

/**
 * Responsible for all of the settings for a collection template.  Can be anything but we should
 * add in anything that is shared between them.
 */
export interface CollectionTemplateSettings {
	defaultLimit: number;
	limitOptions: number[];
	actions?: Action[];
	noResults?: {
		title?: string;
		description?: string;
		showResetFilters?: boolean;
		showCreateButton?: boolean;
	};
	[key: string]: any;
}

export interface QueryParams {
	page?: number;
	limit?: number;
	cursor?: string;
	query?: string;
	mediaType?: string; // Descripes the media type (ex. image, video, etc.)
	segments?: Segment[];
	sortBy?: SortOption;
	name?: string;
	role?: { id: string; name: string };
	[key: string]: any;
	includeVersions?: boolean;
	onlyPublished?: boolean;
	fromReporting?: boolean;
}

// TODO: Move this to the proper place when scaffolded, probably get from API
export interface Segment {
	id: string;
	name: string;
}

// TODO: Move this to the proper place when scaffolded, probably get from API
export interface SortOption {
	id: string;
	direction: string;
}

/**
 * Which direction to sort in.
 * TODO: Move this to the proper place when scaffolded, probably get from API
 */
export enum SortStrategy {
	ASC = 'ASC',
	DESC = 'DESC'
}

export interface Action {
	id: string;
	name: string;
	icon: string;
	type: ActionType;
	methodName?: string;
	methodParams?: any;
	resultMethodName?: string; // The method to call within the (methodName) observable
	setLoad?: boolean;
	classes?: string; // Seperate classes with a space
	showName?: boolean;
	showTooltip?: boolean;
	condition?: Condition; // NOT IMPLEMENTED!
	toggleOptions?: ToggleOptions; // Specific options for toggle buttons
	actionSettings?: any; // Any other settings that are needed for the action
}

export enum ActionType {
	COPY_ID = 'copyId', // Copy the ID of the entity to clipboard
	CREATE = 'create',
	REMOVE = 'remove',
	DOWNLOAD = 'download',
	CLONE = 'clone',
	GO_TO = 'goTo',
	EDIT = 'edit',
	SELECT = 'select', // Special class that will add entity to list of selected items
	VIEW = 'view',
	EXPORT = 'export',
	TOGGLE = 'toggle',
	MENU = 'menu',
	REPORT = 'reporting',
	MERGE = 'merge',
	IS_JOINABLE = 'joinable',
	PIN = 'pin' // Pin/unpin items
}

export interface ToggleOptions {
	condition?: Condition; // NOT IMPLEMENTED: Condition to check to see if this is active
	onIcon: string; // Icon to show when active
	onName: string; // Name to show when active
	offIcon?: string; // Icon to show when inactive (defaults to action.icon)
	offName?: string; // Name to show when inactive (defaults to action.name)
}

export interface ActionEvent {
	entity: any;
	action: Action;
}

export interface Condition {
	path: string;
	relation: ConditionRelation;
	value: any;
}

export enum ConditionRelation {
	GT = 'gt', // >
	GTE = 'gte', // >=
	LT = 'lt', // <
	LTE = 'lte', // <=
	EQ = 'eq', // ===
	NEQ = 'neq' // !==
}
